import { Contributable } from '@/hexapla-core/domains/domain'
import { identifiesLemma, SpanSource } from '@/hexapla-core/domains/lemmas/span'

/**
 * Whether the lemma wizard may pick up the lemma it is holding.
 *
 * The wizard keeps the lemma being built in persisted state and resumes it on
 * every visit. A held id turns each save into an update of that lemma, so
 * resuming the wrong one overwrites finished work. Until the wizard let go of
 * lemmas once they were saved, browsers kept the id of every lemma completed
 * through it, and those ids are still in local storage.
 *
 * A held lemma is resumed when
 * - it has no id: a new lemma, not yet saved;
 * - the contributor opened it with Edit: any status is then intended;
 * - the server still has it as a draft: a saved draft being continued;
 * - the server could not be asked: work is never discarded on a guess.
 *
 * Anything else was left behind — under review or complete, deleted, or no
 * longer this contributor's to edit — and the wizard starts fresh.
 *
 * Pure: the caller asks the server and acts on the answer.
 */

export type LemmaId = string | number | null | undefined

/** What the server said about the held lemma. */
export type HeldLemmaOnServer = { status?: string | null } | 'gone' | 'unreachable'

const hasId = (id: LemmaId): boolean => id !== null && id !== undefined && id !== ''

/** True when the held lemma needs the server's word before it is resumed. */
export const needsServerCheck = (heldId: LemmaId, editingLemmaId: LemmaId): boolean =>
  hasId(heldId) && !(hasId(editingLemmaId) && String(heldId) === String(editingLemmaId))

export const mayResume = (heldId: LemmaId, editingLemmaId: LemmaId, onServer: HeldLemmaOnServer): boolean => {
  if (!needsServerCheck(heldId, editingLemmaId)) return true
  if (onServer === 'unreachable') return true
  if (onServer === 'gone') return false
  return onServer.status === 'draft'
}

/**
 * Whether the wizard's lemma can be saved as a draft yet.
 *
 * A draft needs to say what it is (`identifiesLemma`, the API's own rule) and a
 * text to anchor it: book scoping is derived from the text, and a lemma the API
 * cannot place in a book is refused. Below that line there is nothing to
 * persist; a verse with nothing chosen is unsaved UI state, not a lemma.
 */
export const canSaveDraft = (
  lemma: (Partial<SpanSource> & { hebrew?: string | null; texts?: unknown[] | null }) | null | undefined,
): boolean =>
  !!lemma &&
  identifiesLemma({ lemma: null, lemma_start: null, lemma_stop: null, ...lemma }) &&
  !!(lemma.texts && lemma.texts.length)

/**
 * Who the wizard's lemma is by: its creator once it has one, and whoever
 * changes it now. Every change to the lemma carries it, so no step has to
 * remember to (a lemma with no Greek selected never passes the step that
 * used to set it). Signed out, it says nothing.
 */
export const authorship = (
  lemma: Partial<Contributable>,
  contributorId: number | null | undefined,
): Partial<Contributable> =>
  contributorId === null || contributorId === undefined
    ? {}
    : { create_contributor_id: lemma.create_contributor_id ?? contributorId, update_contributor_id: contributorId }

export default { needsServerCheck, mayResume, canSaveDraft, authorship }
